import { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { CloudSun, Thermometer, Droplets, Wind, Sun, MapPin, Star, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PageHeader } from '../components/layout/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState, EmptyState } from '../components/common/ErrorState';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { getFavoritos, saveFavoritos } from '../services/storage';
import { fetchWithCache } from '../services/cacheService';
import { GeolocationButton } from '../components/GeolocationButton';
import { toast } from 'sonner';
import { API_BASE_URL } from '../config/api';

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;

// Función para crear icono SVG con color personalizado
const createColoredIcon = (color) => {
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 41" width="25" height="41">
      <path fill="${color}" stroke="#fff" stroke-width="1" d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 29 12 29s12-21.8 12-29c0-6.6-5.4-12-12-12z"/>
      <circle fill="#fff" cx="12" cy="12" r="5"/>
    </svg>
  `;
  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34]
  });
};

// Iconos con colores personalizados
const defaultIcon = createColoredIcon('#506f5c');
const selectedIcon = createColoredIcon('#D3A074');

// API Base URL - siempre absoluta para compatibilidad con Capacitor (Android/iOS)
const API_BASE = `${API_BASE_URL}/api/meteo`;

// Alias de sensores a mostrar
const SENSOR_CONFIG = {
  'TEMP': { label: 'Temperatura', unit: 'ºC', icon: Thermometer, color: 'text-red-500' },
  'HUM': { label: 'Humedad', unit: '%', icon: Droplets, color: 'text-blue-500' },
  'RAIN': { label: 'Precipitación', unit: 'mm', icon: CloudSun, color: 'text-cyan-500' },
  'WSP': { label: 'Viento', unit: 'm/s', icon: Wind, color: 'text-slate-500' },
  'RAD': { label: 'Radiación', unit: 'W/m²', icon: Sun, color: 'text-yellow-500' },
};

function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 11, { duration: 0.5 });
    }
  }, [center, map]);
  return null;
}

export function AgroclimaPage() {
  const [estaciones, setEstaciones] = useState([]);
  const [selectedEstacion, setSelectedEstacion] = useState(null);
  const [datosEstacion, setDatosEstacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDatos, setLoadingDatos] = useState(false);
  const [error, setError] = useState(null);
  const [favoritos, setFavoritos] = useState(() => getFavoritos());

  // Recargar favoritos cuando el componente se monta o vuelve a tener foco
  useEffect(() => {
    setFavoritos(getFavoritos());
  }, []);

  // Cargar lista de estaciones desde API oficial
  useEffect(() => {
    loadEstaciones();
  }, []);

  async function loadEstaciones() {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${API_BASE}/stations`;
      const result = await fetchWithCache(url);
      
      const estacionesList = result.data.stations || [];
      
      // Filtrar estaciones activas (sin fecha de desinstalación)
      const estacionesActivas = estacionesList
        .filter(e => !e.date_uninstall || e.date_uninstall === '')
        .map(e => ({
          id: e.id_weatherstation,
          nombre: e.name,
          municipio: e.municipality_name,
          localidad: e.locality,
          lugar: e.place,
          latitud: parseFloat(e.latitude),
          longitud: parseFloat(e.longitude),
          altitud: e.altitude,
          sensoresCount: e.sensors_count
        }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre));
      
      setEstaciones(estacionesActivas);
      
      // Si hay favorito, seleccionarlo
      const fav = getFavoritos();
      if (fav.estacionId) {
        const estacionFav = estacionesActivas.find(e => 
          e.id === parseInt(fav.estacionId) || e.nombre === fav.estacionId
        );
        if (estacionFav) {
          handleSelectEstacion(estacionFav);
        }
      }
    } catch (e) {
      console.error('Error cargando estaciones:', e);
      setError('No se pudieron cargar las estaciones meteorológicas. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }

  async function loadDatosEstacion(estacion, forceRefresh = false) {
    setLoadingDatos(true);
    setDatosEstacion(null);
    
    try {
      // Cargar sensores de la estación
      const sensoresUrl = `${API_BASE}/stations/${estacion.id}/sensors`;
      const sensoresResult = await fetchWithCache(sensoresUrl, { forceRefresh });
      const sensores = sensoresResult.data?.stations?.[0]?.sensors || [];
      
      // Cargar datos resumidos del año actual
      const year = new Date().getFullYear();
      const datosUrl = `${API_BASE}/readings/summarized/station/${estacion.id}/year/${year}`;
      
      let datosResumidos = {};
      try {
        const datosResult = await fetchWithCache(datosUrl, { forceRefresh });
        const summarized = datosResult.data?.summarized;
        
        if (summarized?.sensors) {
          for (const sensor of summarized.sensors) {
            const sensorInfo = sensores.find(s => s.id_weatherstationsensor === sensor.id_weatherstationsensor);
            if (sensorInfo && sensor.dates?.length > 0) {
              // Obtener últimos 30 días con datos válidos
              const ultimos30 = sensor.dates
                .filter(d => {
                  const val = d.values?.[0]?.mean;
                  return val !== null && val !== undefined && val !== '' && typeof val === 'number';
                })
                .slice(-30);
              
              if (ultimos30.length > 0) {
                datosResumidos[sensorInfo.sensor_alias] = {
                  sensor: sensorInfo,
                  datos: ultimos30.map(d => ({
                    fecha: d.observation_date,
                    media: typeof d.values[0].mean === 'number' ? d.values[0].mean : null,
                    min: typeof d.values[0].min === 'number' ? d.values[0].min : null,
                    max: typeof d.values[0].max === 'number' ? d.values[0].max : null,
                    total: d.values[0].total
                  }))
                };
              }
            }
          }
        }
      } catch (datosError) {
        console.warn('Error cargando datos resumidos:', datosError);
      }
      
      setDatosEstacion({
        sensores,
        datosResumidos,
        ultimaActualizacion: new Date().toISOString()
      });
      
      if (Object.keys(datosResumidos).length === 0) {
        console.warn('No hay datos disponibles para la estación', estacion.nombre);
      }
      
    } catch (e) {
      console.error('Error cargando datos de estación:', e);
      toast.error('Error al cargar datos de la estación. Pulsa recargar para intentar de nuevo.');
    } finally {
      setLoadingDatos(false);
    }
  }

  function handleSelectEstacion(estacion) {
    setSelectedEstacion(estacion);
    loadDatosEstacion(estacion);
  }

  function handleReloadEstacion() {
    if (selectedEstacion) {
      toast.info('Recargando datos...');
      loadDatosEstacion(selectedEstacion, true);
    }
  }

  function handleToggleFavorito() {
    if (!selectedEstacion) return;
    
    const currentId = String(selectedEstacion.id);
    const storedId = String(favoritos.estacionId || '');
    const isFavorito = storedId === currentId;
    
    const newFavoritos = saveFavoritos({
      estacionId: isFavorito ? null : currentId,
      estacionNombre: isFavorito ? null : formatNombreEstacion(selectedEstacion.nombre)
    });
    setFavoritos(newFavoritos);
    toast.success(isFavorito ? 'Favorito eliminado' : 'Estación guardada como favorita');
  }

  function formatNombreEstacion(nombre) {
    return nombre
      .replace(/_/g, ' ')
      .replace(/01$/i, '')
      .replace(/02$/i, '')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  }

  // Obtener último valor de cada sensor
  const ultimosValores = useMemo(() => {
    if (!datosEstacion?.datosResumidos) return {};
    
    const valores = {};
    for (const [alias, data] of Object.entries(datosEstacion.datosResumidos)) {
      if (data.datos?.length > 0) {
        const ultimo = data.datos[data.datos.length - 1];
        // Solo añadir si tiene valor válido
        if (ultimo.media !== null && ultimo.media !== undefined && ultimo.media !== '') {
          valores[alias] = {
            ...ultimo,
            sensor: data.sensor,
            config: SENSOR_CONFIG[alias]
          };
        }
      }
    }
    return valores;
  }, [datosEstacion]);

  // Datos para gráfica de temperatura
  const datosGrafica = useMemo(() => {
    if (!datosEstacion?.datosResumidos?.TEMP?.datos) return [];
    
    return datosEstacion.datosResumidos.TEMP.datos
      .filter(d => d.media !== null && d.media !== undefined && d.media !== '' && typeof d.media === 'number')
      .map(d => ({
        fecha: new Date(d.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
        media: parseFloat(Number(d.media).toFixed(1)),
        min: d.min !== null && d.min !== '' ? parseFloat(Number(d.min).toFixed(1)) : null,
        max: d.max !== null && d.max !== '' ? parseFloat(Number(d.max).toFixed(1)) : null,
      }));
  }, [datosEstacion]);

  const mapCenter = selectedEstacion 
    ? [selectedEstacion.latitud, selectedEstacion.longitud]
    : [28.35, -16.5];

  if (loading) {
    return (
      <div className="px-4 py-4">
        <PageHeader title="Agroclima" subtitle="Estaciones meteorológicas" />
        <LoadingState message="Cargando estaciones del Cabildo..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-4">
        <PageHeader title="Agroclima" subtitle="Estaciones meteorológicas" />
        <ErrorState message={error} onRetry={loadEstaciones} />
      </div>
    );
  }

  return (
    <div className="px-4 py-4" data-testid="agroclima-page" role="main">
      <PageHeader 
        title="Agroclima" 
        subtitle={selectedEstacion 
          ? formatNombreEstacion(selectedEstacion.nombre) 
          : `${estaciones.length} estaciones activas`
        }
        showBack
      />

      {/* Instrucciones */}
      {!selectedEstacion && (
        <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border" role="region" aria-label="Instrucciones">
          <p className="text-sm font-medium">Seleccione una estación meteorológica:</p>
          <p className="text-sm text-muted-foreground mt-1">
            Para consultar los datos agroclimáticos, elija una estación en el mapa. 
            Una vez seleccionada, los valores correspondientes se mostrarán automáticamente debajo del mapa.
          </p>
        </div>
      )}

      {/* Botón recargar */}
      <div className="flex justify-end mt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={loadEstaciones}
          aria-label="Recargar datos de estaciones"
          className="focus-visible:ring-2 focus-visible:ring-primary"
        >
          <RefreshCw size={16} aria-hidden="true" />
        </Button>
      </div>

      {/* Mapa */}
      <div 
        className="mt-2 h-[350px] rounded-xl overflow-hidden border border-border relative"
        role="application"
        aria-label="Mapa de estaciones meteorológicas de Tenerife"
      >
        <MapContainer 
          center={mapCenter} 
          zoom={selectedEstacion ? 11 : 9} 
          className="h-full w-full"
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <GeolocationButton />
          <MapUpdater center={selectedEstacion ? mapCenter : null} />
          
          {estaciones.map(estacion => (
            <Marker
              key={estacion.id}
              position={[estacion.latitud, estacion.longitud]}
              icon={selectedEstacion?.id === estacion.id ? selectedIcon : defaultIcon}
              eventHandlers={{
                click: () => handleSelectEstacion(estacion)
              }}
            >
              <Popup>
                <div className="text-sm">
                  <strong>{formatNombreEstacion(estacion.nombre)}</strong>
                  <br />
                  <span className="text-muted-foreground">{estacion.municipio}</span>
                  <br />
                  <Badge variant="outline" className="mt-1">
                    {estacion.altitud}m
                  </Badge>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Detalle de estación seleccionada */}
      {selectedEstacion && (
        <section className="mt-4 space-y-4" aria-label={`Datos de la estación ${formatNombreEstacion(selectedEstacion.nombre)}`}>
          {/* Header con favorito y recargar */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold">{formatNombreEstacion(selectedEstacion.nombre)}</h2>
              <p className="text-sm text-muted-foreground">
                {selectedEstacion.municipio} · {selectedEstacion.altitud}m
                {selectedEstacion.lugar && ` · ${selectedEstacion.lugar}`}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReloadEstacion}
                disabled={loadingDatos}
                aria-label="Recargar datos de la estación"
                className="focus-visible:ring-2 focus-visible:ring-primary"
              >
                <RefreshCw size={16} className={loadingDatos ? 'animate-spin' : ''} aria-hidden="true" />
              </Button>
              <Button
                variant={String(favoritos.estacionId || '') === String(selectedEstacion.id) ? 'default' : 'outline'}
                size="sm"
                onClick={handleToggleFavorito}
                data-testid="toggle-favorito"
                aria-label={String(favoritos.estacionId || '') === String(selectedEstacion.id) 
                  ? 'Eliminar de favoritos' 
                  : 'Guardar como favorito'}
                aria-pressed={String(favoritos.estacionId || '') === String(selectedEstacion.id)}
                className="focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Star 
                  size={16} 
                  className={String(favoritos.estacionId || '') === String(selectedEstacion.id) ? 'fill-current' : ''}
                  aria-hidden="true"
                />
              </Button>
            </div>
          </div>

          {loadingDatos ? (
            <LoadingState message="Cargando datos meteorológicos..." />
          ) : datosEstacion ? (
            <>
              {/* Valores actuales */}
              {Object.keys(ultimosValores).length > 0 ? (
                <>
                  {/* Fecha de la medición */}
                  <div className="text-center text-sm text-muted-foreground" aria-live="polite">
                    Datos del {(() => {
                      const primerValor = Object.values(ultimosValores)[0];
                      if (primerValor?.fecha) {
                        return new Date(primerValor.fecha).toLocaleDateString('es-ES', { 
                          weekday: 'long', 
                          day: 'numeric', 
                          month: 'long',
                          year: 'numeric'
                        });
                      }
                      return '-';
                    })()}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="list" aria-label="Valores meteorológicos actuales">
                    {Object.entries(ultimosValores).map(([alias, data]) => {
                      const config = data.config || {};
                      const Icon = config.icon || CloudSun;
                      return (
                        <Card key={alias} role="listitem">
                          <CardContent className="p-3 text-center">
                            <Icon className={`h-5 w-5 mx-auto mb-1 ${config.color || 'text-muted-foreground'}`} aria-hidden="true" />
                            <p className="text-xl font-bold font-mono" aria-label={`${config.label || alias}: ${data.media !== null ? data.media.toFixed(1) : 'sin datos'} ${config.unit || ''}`}>
                              {data.media !== null ? data.media.toFixed(1) : '-'}
                              <span className="text-xs font-normal ml-1" aria-hidden="true">{config.unit || ''}</span>
                            </p>
                            <p className="text-xs text-muted-foreground">{config.label || alias}</p>
                            {data.min !== null && data.max !== null && (
                              <p className="text-xs text-muted-foreground" aria-label={`Rango: mínimo ${data.min.toFixed(1)}, máximo ${data.max.toFixed(1)}`}>
                                {data.min.toFixed(1)} - {data.max.toFixed(1)}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No hay datos disponibles para esta estación en el período actual.
                  </AlertDescription>
                </Alert>
              )}

              {/* Gráfica de temperatura */}
              {datosGrafica.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Thermometer size={18} className="text-red-500" />
                      Temperatura - Últimos {datosGrafica.length} días
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={datosGrafica}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis 
                            dataKey="fecha" 
                            tick={{ fontSize: 10 }}
                            interval="preserveStartEnd"
                          />
                          <YAxis 
                            tick={{ fontSize: 11 }}
                            domain={['auto', 'auto']}
                            tickFormatter={(v) => `${v}°`}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              background: 'hsl(var(--background))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                            formatter={(value, name) => {
                              const labels = { media: 'Media', min: 'Mínima', max: 'Máxima' };
                              return [`${value}°C`, labels[name] || name];
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="max" 
                            stroke="#ef4444" 
                            strokeWidth={1}
                            dot={false}
                            name="max"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="media" 
                            stroke="#f97316" 
                            strokeWidth={2}
                            dot={false}
                            name="media"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="min" 
                            stroke="#3b82f6" 
                            strokeWidth={1}
                            dot={false}
                            name="min"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tabla de datos recientes */}
              {datosGrafica.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Datos recientes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[150px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Fecha</TableHead>
                            <TableHead className="text-right">Mín</TableHead>
                            <TableHead className="text-right">Media</TableHead>
                            <TableHead className="text-right">Máx</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {datosGrafica.slice().reverse().slice(0, 10).map((d, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-medium">{d.fecha}</TableCell>
                              <TableCell className="text-right font-mono text-blue-600">{d.min}°</TableCell>
                              <TableCell className="text-right font-mono">{d.media}°</TableCell>
                              <TableCell className="text-right font-mono text-red-600">{d.max}°</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </section>
      )}

      {!selectedEstacion && (
        <div className="mt-6">
          <EmptyState 
            title="Selecciona una estación"
            message="Elige una estación en el mapa o la lista para ver sus datos meteorológicos"
            icon={MapPin}
          />
        </div>
      )}

      {/* Fuente de datos */}
      <div className="mt-4 text-center text-xs text-muted-foreground">
        Fuente: <a href="https://datos.tenerife.es" target="_blank" rel="noopener noreferrer" className="hover:text-primary underline">Cabildo de Tenerife – Datos abiertos (estaciones meteorológicas)</a>
      </div>
    </div>
  );
}

export default AgroclimaPage;
